import { type FC, useEffect, useRef, useState } from "react";
import { Platform, Pressable } from "react-native";
import { StyleSheet } from "react-native-unistyles";
import "../../theme";
import { Text } from "../Text";
import type { OptionState } from "./types";

interface AutocompleteOptionProps {
  readonly label: string;
  readonly state: OptionState;
  readonly onPress: () => void;
  readonly onHoverIn?: (() => void) | undefined;
}

const AutocompleteOption: FC<AutocompleteOptionProps> = ({ label, state, onPress, onHoverIn }) => {
  const [hovered, setHovered] = useState(false);
  const ref = useRef<React.ComponentRef<typeof Pressable>>(null);

  useEffect(() => {
    if (state.focused && Platform.OS === "web" && ref.current) {
      (ref.current as unknown as HTMLElement).scrollIntoView({ block: "nearest" });
    }
  }, [state.focused]);

  return (
    <Pressable
      ref={ref}
      onPress={onPress}
      onHoverIn={() => {
        setHovered(true);
        onHoverIn?.();
      }}
      onHoverOut={() => setHovered(false)}
      style={[styles.option, (state.focused || hovered) && styles.optionFocused]}
    >
      <Text textStyle={state.selected ? "sm/medium" : "sm/regular"} color="text" numberOfLines={1}>
        {label}
      </Text>
    </Pressable>
  );
};

AutocompleteOption.displayName = "AutocompleteOption";

export { AutocompleteOption };

const styles = StyleSheet.create((theme) => ({
  option: {
    paddingHorizontal: theme.spacing["3xl"],
    paddingVertical: theme.spacing.xl,
    userSelect: "none",
  },
  optionFocused: {
    backgroundColor: theme.colors.surface2.hover,
  },
}));
